import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import type { UseMutationResult } from "@tanstack/react-query";
import type {
  ClientJobRole,
  ClientUser,
  EnabledServices,
  ResetUserPasswordResponse,
  ServiceOverrides,
} from "../api/clientManagement";

type ServiceKey = keyof EnabledServices;

interface UserEditDialogProps {
  open: boolean;
  user: ClientUser | null;
  isCentralAdmin: boolean;
  departments: { id: string; name: string }[];
  jobRoles: ClientJobRole[];
  regions: { id: string; name: string }[];
  depots: { id: string; name: string }[];
  updateUserServicesMutation: UseMutationResult<
    unknown,
    unknown,
    { userId: string; payload: Partial<ServiceOverrides> }
  >;
  resetUserPasswordMutation: UseMutationResult<
    ResetUserPasswordResponse,
    unknown,
    {
      userId: string;
      temporary: boolean;
      sendEmail: boolean;
      password?: string;
      loginUrl?: string;
    }
  >;
  updatingUserServicesId: string | null;
  assignUserToDepartmentMutation: UseMutationResult<
    unknown,
    unknown,
    { departmentId: string; userId: string }
  >;
  removeUserFromDepartmentMutation: UseMutationResult<
    unknown,
    unknown,
    { departmentId: string; userId: string }
  >;
  assignUserToJobRoleMutation: UseMutationResult<
    unknown,
    unknown,
    { jobRoleId: string; userId: string }
  >;
  removeUserFromJobRoleMutation: UseMutationResult<
    unknown,
    unknown,
    { jobRoleId: string; userId: string }
  >;
  assignUserToRegionMutation: UseMutationResult<
    unknown,
    unknown,
    { regionId: string; userId: string }
  >;
  removeUserFromRegionMutation: UseMutationResult<
    unknown,
    unknown,
    { regionId: string; userId: string }
  >;
  assignUserToDepotMutation: UseMutationResult<
    unknown,
    unknown,
    { depotId: string; userId: string }
  >;
  removeUserFromDepotMutation: UseMutationResult<
    unknown,
    unknown,
    { depotId: string; userId: string }
  >;
  removeUserFromClientMutation: UseMutationResult<unknown, unknown, string>;
  deleteUserMutation: UseMutationResult<unknown, unknown, string>;
  updateUserAdminMutation: UseMutationResult<
    unknown,
    unknown,
    { userId: string; isAdmin: boolean }
  >;
  onClose: () => void;
}

export const UserEditDialog: React.FC<UserEditDialogProps> = ({
  open,
  user,
  isCentralAdmin,
  departments,
  jobRoles,
  regions,
  depots,
  updateUserServicesMutation,
  resetUserPasswordMutation,
  updatingUserServicesId,
  assignUserToDepartmentMutation,
  removeUserFromDepartmentMutation,
  assignUserToJobRoleMutation,
  removeUserFromJobRoleMutation,
  assignUserToRegionMutation,
  removeUserFromRegionMutation,
  assignUserToDepotMutation,
  removeUserFromDepotMutation,
  removeUserFromClientMutation,
  deleteUserMutation,
  updateUserAdminMutation,
  onClose,
}) => {
  const [departmentSelection, setDepartmentSelection] = useState("");
  const [jobRoleSelection, setJobRoleSelection] = useState("");
  const [regionSelection, setRegionSelection] = useState("");
  const [depotSelection, setDepotSelection] = useState("");
  const [passwordMode, setPasswordMode] = useState<"temporary" | "permanent">(
    "temporary"
  );
  const [sendPasswordEmail, setSendPasswordEmail] = useState<"yes" | "no">("yes");
  const [passwordSource, setPasswordSource] = useState<"random" | "manual">(
    "random"
  );
  const [manualPassword, setManualPassword] = useState("");
  const [passwordResetResult, setPasswordResetResult] = useState<
    ResetUserPasswordResponse["data"] | null
  >(null);
  const [serviceOverrides, setServiceOverrides] = useState<ServiceOverrides>({
    learning_locker: null,
    occupational_health: null,
    wearables: null,
    akea_life: null,
  });
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (!user || !open) return;
    setDepartmentSelection("");
    setJobRoleSelection("");
    setRegionSelection("");
    setDepotSelection("");
    setPasswordMode("temporary");
    setSendPasswordEmail("yes");
    setPasswordSource("random");
    setManualPassword("");
    setPasswordResetResult(null);
    setIsAdmin(Boolean(user.is_admin));
    setServiceOverrides({
      learning_locker: user.service_overrides?.learning_locker ?? null,
      occupational_health: user.service_overrides?.occupational_health ?? null,
      wearables: user.service_overrides?.wearables ?? null,
      akea_life: user.service_overrides?.akea_life ?? null,
    });
  }, [open, user]);

  if (!user) return null;

  const isSavingServices = updatingUserServicesId === user.id;
  const isResettingPassword = resetUserPasswordMutation.isPending;
  const enabledServices = user.enabled_services ?? {
    learning_locker: false,
    occupational_health: false,
    wearables: false,
    akea_life: false,
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Stack spacing={0.5}>
          <Typography variant="h6">
            Edit {user.first_name} {user.last_name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user.email}
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          <Alert severity="info">
            Update department, job role and services for this user.
          </Alert>

          <Stack spacing={1.5}>
            <Typography variant="subtitle2">Device connection</Typography>
            <TextField
              label="First connected"
              size="small"
              fullWidth
              value={
                user.first_connected_at
                  ? new Date(user.first_connected_at).toLocaleDateString()
                  : "Not yet connected"
              }
              helperText="When this user first connected a wearable device"
              slotProps={{ input: { readOnly: true } }}
            />
          </Stack>

          <Stack spacing={1.5}>
            <Typography variant="subtitle2">Departments</Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <FormControl size="small" fullWidth>
                <Select
                  value={departmentSelection}
                  displayEmpty
                  onChange={(event) => setDepartmentSelection(event.target.value)}
                  renderValue={(value) => {
                    if (!value) return "Select department";
                    return departments.find((dept) => dept.id === value)?.name ?? value;
                  }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                disabled={!departmentSelection}
                onClick={() =>
                  assignUserToDepartmentMutation.mutate({
                    departmentId: departmentSelection,
                    userId: user.id,
                  })
                }
              >
                Assign
              </Button>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {user.departments.length ? (
                user.departments.map((dept) => (
                  <Button
                    key={dept.id}
                    variant="text"
                    size="small"
                    onClick={() =>
                      removeUserFromDepartmentMutation.mutate({
                        departmentId: dept.id,
                        userId: user.id,
                      })
                    }
                  >
                    Remove {dept.name}
                  </Button>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No departments assigned.
                </Typography>
              )}
            </Stack>
          </Stack>

          <Stack spacing={1.5}>
            <Typography variant="subtitle2">Regions</Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <FormControl size="small" fullWidth>
                <Select
                  value={regionSelection}
                  displayEmpty
                  onChange={(event) => setRegionSelection(event.target.value)}
                  renderValue={(value) => {
                    if (!value) return "Select region";
                    return regions.find((r) => r.id === value)?.name ?? value;
                  }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {regions.map((region) => (
                    <MenuItem key={region.id} value={region.id}>
                      {region.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                disabled={!regionSelection}
                onClick={() =>
                  assignUserToRegionMutation.mutate({
                    regionId: regionSelection,
                    userId: user.id,
                  })
                }
              >
                Assign
              </Button>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {user.regions?.length ? (
                user.regions.map((region) => (
                  <Button
                    key={region.id}
                    variant="text"
                    size="small"
                    onClick={() =>
                      removeUserFromRegionMutation.mutate({
                        regionId: region.id,
                        userId: user.id,
                      })
                    }
                  >
                    Remove {region.name}
                  </Button>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No regions assigned.
                </Typography>
              )}
            </Stack>
          </Stack>

          <Stack spacing={1.5}>
            <Typography variant="subtitle2">Depots</Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <FormControl size="small" fullWidth>
                <Select
                  value={depotSelection}
                  displayEmpty
                  onChange={(event) => setDepotSelection(event.target.value)}
                  renderValue={(value) => {
                    if (!value) return "Select depot";
                    return depots.find((d) => d.id === value)?.name ?? value;
                  }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {depots.map((depot) => (
                    <MenuItem key={depot.id} value={depot.id}>
                      {depot.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                disabled={!depotSelection}
                onClick={() =>
                  assignUserToDepotMutation.mutate({
                    depotId: depotSelection,
                    userId: user.id,
                  })
                }
              >
                Assign
              </Button>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {user.depots?.length ? (
                user.depots.map((depot) => (
                  <Button
                    key={depot.id}
                    variant="text"
                    size="small"
                    onClick={() =>
                      removeUserFromDepotMutation.mutate({
                        depotId: depot.id,
                        userId: user.id,
                      })
                    }
                  >
                    Remove {depot.name}
                  </Button>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No depots assigned.
                </Typography>
              )}
            </Stack>
          </Stack>

          <Stack spacing={1.5}>
            <Typography variant="subtitle2">Job Roles</Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <FormControl size="small" fullWidth>
                <Select
                  value={jobRoleSelection}
                  displayEmpty
                  onChange={(event) => setJobRoleSelection(event.target.value)}
                  renderValue={(value) => {
                    if (!value) return "Select job role";
                    return jobRoles.find((role) => role.id === value)?.name ?? value;
                  }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {jobRoles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                disabled={!jobRoleSelection}
                onClick={() =>
                  assignUserToJobRoleMutation.mutate({
                    jobRoleId: jobRoleSelection,
                    userId: user.id,
                  })
                }
              >
                Assign
              </Button>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {user.job_roles.length ? (
                user.job_roles.map((role) => (
                  <Button
                    key={role.id}
                    variant="text"
                    size="small"
                    onClick={() =>
                      removeUserFromJobRoleMutation.mutate({
                        jobRoleId: role.id,
                        userId: user.id,
                      })
                    }
                  >
                    Remove {role.name}
                  </Button>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No job roles assigned.
                </Typography>
              )}
            </Stack>
          </Stack>

          <Stack spacing={1.5}>
            <Typography variant="subtitle2">Password</Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <FormControl size="small" fullWidth>
                <Select
                  value={passwordMode}
                  onChange={(event) =>
                    setPasswordMode(event.target.value as "temporary" | "permanent")
                  }
                >
                  <MenuItem value="temporary">Temporary password</MenuItem>
                  <MenuItem value="permanent">Permanent password</MenuItem>
                </Select>
                <FormHelperText>
                  Temporary forces a password change at next sign-in.
                </FormHelperText>
              </FormControl>
              <FormControl size="small" fullWidth>
                <Select
                  value={sendPasswordEmail}
                  onChange={(event) =>
                    setSendPasswordEmail(event.target.value as "yes" | "no")
                  }
                >
                  <MenuItem value="yes">Send password email</MenuItem>
                  <MenuItem value="no">Do not send email</MenuItem>
                </Select>
                <FormHelperText>
                  Choose whether the password should be emailed.
                </FormHelperText>
              </FormControl>
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <FormControl size="small" fullWidth>
                <Select
                  value={passwordSource}
                  onChange={(event) =>
                    setPasswordSource(event.target.value as "random" | "manual")
                  }
                >
                  <MenuItem value="random">Randomise password</MenuItem>
                  <MenuItem value="manual">Set password manually</MenuItem>
                </Select>
                <FormHelperText>
                  Choose whether to randomise or set a specific password.
                </FormHelperText>
              </FormControl>
              {passwordSource === "manual" ? (
                <TextField
                  size="small"
                  fullWidth
                  label="Password"
                  value={manualPassword}
                  onChange={(event) => setManualPassword(event.target.value)}
                />
              ) : null}
            </Stack>
            <Button
              variant="outlined"
              disabled={
                isResettingPassword ||
                (passwordSource === "manual" && !manualPassword.trim())
              }
              onClick={() => {
                const temporary = passwordMode === "temporary";
                const sendEmail = sendPasswordEmail === "yes";
                const password =
                  passwordSource === "manual" ? manualPassword.trim() : undefined;
                const loginUrl =
                  typeof window !== "undefined"
                    ? `${window.location.origin}/login`
                    : undefined;
                const confirmed = window.confirm(
                  `Reset password for ${user.email} as ${
                    temporary ? "temporary" : "permanent"
                  } using ${passwordSource === "manual" ? "manual password" : "random password"}${
                    sendEmail ? " and send email" : ""
                  }?`
                );
                if (!confirmed) return;
                setPasswordResetResult(null);
                resetUserPasswordMutation.mutate(
                  { userId: user.id, temporary, sendEmail, password, loginUrl },
                  {
                    onSuccess: (response) => setPasswordResetResult(response.data),
                  }
                );
              }}
            >
              {isResettingPassword ? "Resetting..." : "Reset Password"}
            </Button>
            {passwordResetResult ? (
              <Alert severity={passwordResetResult.email_sent ? "success" : "warning"}>
                {passwordResetResult.email_sent
                  ? "Password reset complete and email sent."
                  : passwordResetResult.send_email
                    ? `Password reset complete, but email failed: ${
                        passwordResetResult.email_error || "Unknown error"
                      }`
                    : "Password reset complete. Email was not sent."}
                {passwordResetResult.generated_password ? (
                  <>
                    {" "}
                    Password: <strong>{passwordResetResult.generated_password}</strong>
                  </>
                ) : null}
              </Alert>
            ) : null}
            {resetUserPasswordMutation.isError && !passwordResetResult ? (
              <Alert severity="error">Failed to reset password.</Alert>
            ) : null}
          </Stack>

          <Stack spacing={1.5}>
            <Typography variant="subtitle2">Services</Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 1.5,
              }}
            >
              {SERVICE_FIELDS.map((service) => {
                const value = toSelectValue(serviceOverrides[service.key]);
                const effectiveLabel = enabledServices[service.key]
                  ? "Enabled"
                  : "Disabled";
                return (
                  <FormControl key={service.key} size="small" fullWidth>
                    <Select
                      value={value}
                      onChange={(event) =>
                        setServiceOverrides((prev) => ({
                          ...prev,
                          [service.key]: fromSelectValue(event.target.value),
                        }))
                      }
                      disabled={!isCentralAdmin || isSavingServices}
                    >
                      <MenuItem value="default">Use client default</MenuItem>
                      <MenuItem value="enabled">Enabled</MenuItem>
                      <MenuItem value="disabled">Disabled</MenuItem>
                    </Select>
                    <FormHelperText>
                      {service.label}: {effectiveLabel}
                    </FormHelperText>
                  </FormControl>
                );
              })}
            </Box>
            {isCentralAdmin ? (
              <Button
                variant="outlined"
                onClick={() =>
                  updateUserServicesMutation.mutate({
                    userId: user.id,
                    payload: serviceOverrides,
                  })
                }
                disabled={isSavingServices}
              >
                Save Services
              </Button>
            ) : null}
          </Stack>

          {isCentralAdmin && (
            <Stack spacing={1.5}>
              <Typography variant="subtitle2">Admin Access</Typography>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={isAdmin}
                      disabled={updateUserAdminMutation.isPending}
                      onChange={(e) => {
                        const nextValue = e.target.checked;
                        setIsAdmin(nextValue);
                        updateUserAdminMutation.mutate(
                          { userId: user.id, isAdmin: nextValue },
                          { onError: () => setIsAdmin(!nextValue) }
                        );
                      }}
                    />
                  }
                  label="Admin"
                />
                {isAdmin && (
                  <Chip label="Admin" size="small" color="warning" variant="filled" sx={{ fontSize: 11, height: 20 }} />
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Admins can access management dashboards and client settings.
              </Typography>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <Stack direction="row" spacing={1}>
          <Button
            color="error"
            onClick={() =>
              removeUserFromClientMutation.mutate(user.id, { onSuccess: onClose })
            }
          >
            Remove From Client
          </Button>
          {isCentralAdmin ? (
            <Button
              color="error"
              variant="contained"
              disabled={deleteUserMutation.isPending}
              onClick={() => {
                const confirmed = window.confirm(
                  `Delete user ${user.email}? This permanently removes their account from Keycloak and deletes their data.`
                );
                if (!confirmed) return;
                deleteUserMutation.mutate(user.id, { onSuccess: onClose });
              }}
            >
              {deleteUserMutation.isPending ? "Deleting..." : "Delete User"}
            </Button>
          ) : null}
        </Stack>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const SERVICE_FIELDS: { key: ServiceKey; label: string }[] = [
  { key: "learning_locker", label: "Learning Locker" },
  { key: "occupational_health", label: "Occupational Health" },
  { key: "wearables", label: "Wearables" },
  { key: "akea_life", label: "Akea Life" },
];

const toSelectValue = (
  value: boolean | null,
): "default" | "enabled" | "disabled" => {
  if (value === null) return "default";
  return value ? "enabled" : "disabled";
};

const fromSelectValue = (value: unknown): boolean | null => {
  if (value === "enabled") return true;
  if (value === "disabled") return false;
  return null;
};
